import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { usePermission } from '../../components/permissions/PermissionTooltip';
import { onSnapshot, getDoc, updateDoc, serverTimestamp, query, orderBy, limit } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions, auth } from '../../firebase';
import { CLOUD_FUNCTIONS, CALLABLE_TIMEOUTS } from '../../constants/firebase';
import {
  projectDoc as projectDocRef,
  protocolAnalysisLatestDoc,
  protocolVersionsCollection,
  protocolComparisonsCollection,
} from '../../constants/firestorePaths';
import StepProgress from '../../components/StepProgress';
import {
  DOCUMENT_KIND_CHOICES,
  DOCUMENT_KIND_PROMPT,
  DOCUMENT_KIND_WHY_ASKED,
  REANALYSE_ACTION_LABEL,
  adoptionBlockedReason,
  adoptionConfirmation,
  describeComparison,
  describeVersionHistory,
  kindSelectionError,
  reanalysisWarning,
  reopenedComparisonState,
  comparisonFromStoredRecord,
  reopenedFormState,
  nextAvailableLabel,
  type ProtocolComparisonLike,
  type ProtocolDocumentKind,
  type ProtocolVersionLike,
  type VersionView,
} from '../../utils/protocolAmendments';
import {
  Upload,
  FileText,
  CheckCircle2,
  Circle,
  Loader2,
  Target,
  Users,
  Calendar,
  Shield,
  ArrowRight,
  Beaker,
  Activity,
  X,
  GitBranch,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  ListChecks,
  RefreshCw,
  Download,
  FileClock,
} from 'lucide-react';

// ============================================================
// Protocol requirements (Phase 1 of the RTM feature)
//
// Local re-declaration of the ProtocolRequirement shape from
// functions/src/models/rtmTypes.ts. The frontend doesn't import from
// functions/, so this mirrors the canonical type. Keep in sync if the
// backend type changes.
// ============================================================

type ProtocolRequirementType =
  | 'objective_primary'
  | 'objective_secondary'
  | 'endpoint_primary'
  | 'endpoint_secondary'
  | 'inclusion_criterion'
  | 'exclusion_criterion'
  | 'safety_assessment'
  | 'safety_reporting'
  | 'efficacy_assessment'
  | 'visit_assessment';

type ProtocolRequirementStatus = 'active' | 'superseded' | 'removed';

interface ProtocolRequirement {
  id: string;
  type: ProtocolRequirementType;
  text: string;
  version: number;
  status: ProtocolRequirementStatus;
  changeReason?: string;
  // createdAt comes back as a Firestore Timestamp serialized to {seconds, nanoseconds}
  createdAt?: { seconds: number; nanoseconds: number } | unknown;
}

interface RequirementGroup {
  type: ProtocolRequirementType;
  label: string;
  items: ProtocolRequirement[];
}

// Order here drives display order in the Requirements panel and matches
// the extractor's walk order, so REQ-IDs appear in natural progression.
const REQUIREMENT_GROUP_ORDER: Array<{ type: ProtocolRequirementType; label: string }> = [
  { type: 'objective_primary', label: 'Primary Objectives' },
  { type: 'objective_secondary', label: 'Secondary Objectives' },
  { type: 'endpoint_primary', label: 'Primary Endpoints' },
  { type: 'endpoint_secondary', label: 'Secondary Endpoints' },
  { type: 'inclusion_criterion', label: 'Inclusion Criteria' },
  { type: 'exclusion_criterion', label: 'Exclusion Criteria' },
  { type: 'safety_assessment', label: 'Safety Assessments' },
  { type: 'safety_reporting', label: 'Safety Reporting' },
  { type: 'efficacy_assessment', label: 'Procedures & Lab Tests' },
  { type: 'visit_assessment', label: 'Visit Assessments' },
];

interface ProtocolAnalysis {
  studyTitle?: string;
  studyDesign?: {
    type?: string;
    phase?: string;
    blinding?: string;
    randomization?: string;
    numberOfArms?: number;
    controlType?: string;
    designFeatures?: string[];
  };
  objectives?: {
    primary?: string[];
    secondary?: string[];
    exploratory?: string[];
  };
  endpoints?: {
    primary?: string[];
    secondary?: string[];
    exploratory?: string[];
  };
  interventions?: Array<{
    name?: string;
    dose?: string;
    route?: string;
    frequency?: string;
    duration?: string;
  }>;
  arms?: Array<{
    name?: string;
    description?: string;
    ratio?: number;
  }>;
  population?: {
    targetSize?: number;
    targetEnrollment?: number;
    inclusion?: string[];
    exclusion?: string[];
    /** Set when a list is empty because it was not extracted, not because the protocol has none. */
    extractionStatus?: { inclusion?: string; exclusion?: string };
    ageRange?: string;
    gender?: string;
    keyCondition?: string;
  };
  schedule?: {
    duration?: string;
    studyDuration?: string;
    visits?: any[];
    totalVisits?: number;
  };
  safety?: {
    aesOfInterest?: string[];
    stoppingRules?: string[];
    assessments?: string[];
    reporting?: string[];
    dsmb?: boolean;
    monitoringPlan?: string;
  };
  labTests?: string[];
  procedures?: string[];
  questionnaires?: string[];
  contacts?: {
    sponsor?: string;
    piName?: string;
    piPhone?: string;
    piEmail?: string;
    irbName?: string;
    irbPhone?: string;
    irbEmail?: string;
  };
  dataCollection?: {
    biomarkers?: string[];
    sampleTypes?: string[];
    imagingStudies?: string[];
    pkSampling?: string[];
  };
  concomitantMedications?: {
    prohibited?: string[];
    allowedWithRestrictions?: string[];
    requiredBackground?: string[];
  };
  stratificationFactors?: string[];
  dosageModifications?: string[];
  withdrawalCriteria?: string[];
  dataCollectionSchedule?: Array<{
    procedure?: string;
    visits?: string[];
  }>;
  extractionMetadata?: {
    passesRun?: number;
    totalItemsExtracted?: number;
    itemsAddedInVerification?: number;
    completenessConfidence?: string;
    verificationNotes?: string[];
  };
}

// ============================================================
// Amendments panel
//
// A sponsor sends an amendment; this is where it arrives. Upload it, say which
// KIND of document it is, see what changed against the version the study
// currently holds, and adopt it.
//
// EVERY STRING ON THIS PANEL COMES FROM `utils/protocolAmendments.ts`, and that
// is the design rather than a tidiness preference. `web` has no test runner, so
// wording written inline here is wording nothing can hold to account — and the
// wording is the product on this screen: what adoption promises, what it
// refuses to promise, and whether a comparison that could not be saved offers a
// link that goes nowhere. The component fetches, renders and nothing else.
//
// WHAT ADOPTION DOES NOT DO, AND WHY THE PANEL LABOURS THE POINT.
// `functions/src/protocol/adoptAmendment.ts` decided that adoption must not
// re-analyse the protocol: `analyzeProtocolV2` rewrites the study's name, arms
// and Schedule of Events with no undo, and bundling that into a records act
// gives a person something they did not ask for. Its answer to "then adoption
// feels inert" is to SHOW what happened — the new version, the archived one
// still downloadable, the trial-folder filing, and the flagged artifacts as a
// to-do list. That is what this panel renders after an adoption, and it is why
// re-analysis is a separate button with its own warning rather than a step in
// the same flow. Never a cascade.
// ============================================================

/** `adoptAmendmentV2`'s response. Mirrors `AdoptAmendmentResponse` server-side. */
interface AdoptAmendmentResult {
  status: 'adopted' | 'unchanged';
  versionId: string;
  label: string;
  supersededVersion: { versionId: string; label: string; storagePath: string } | null;
  affectedArtifacts: string[];
  filedInTmf: boolean;
}

/** One row of the version history. */
function VersionRow({
  view,
  onDownload,
  downloading,
}: {
  view: VersionView;
  onDownload: (view: VersionView) => void;
  downloading: string | null;
}) {
  return (
    <div
      className={`rounded-lg border p-4 ${
        view.current ? 'border-primary-200 bg-primary-50' : 'border-gray-200 bg-white'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center flex-wrap gap-2">
            <span className="font-semibold text-gray-900">{view.label || 'Unlabelled version'}</span>
            <span
              className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                view.current ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600'
              }`}
            >
              {view.statusLabel}
            </span>
            <span className="text-xs text-gray-500">{view.kindLabel}</span>
          </div>
          <p className="text-sm text-gray-600 mt-1">{view.attribution}</p>
          {!view.current && view.supersededOn && (
            <p className="text-xs text-gray-500 mt-0.5">Superseded {view.supersededOn}</p>
          )}
          <p className="text-xs text-gray-500 mt-0.5">{view.filedNote}</p>
        </div>
        {view.download.available ? (
          <button
            type="button"
            onClick={() => onDownload(view)}
            disabled={downloading === view.id}
            className="inline-flex items-center space-x-2 px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-60 flex-shrink-0"
          >
            {downloading === view.id ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            <span>Download</span>
          </button>
        ) : (
          <span
            className="text-xs text-gray-500 max-w-[16rem] flex-shrink-0"
            title={view.download.unavailableReason}
          >
            {view.download.unavailableReason}
          </span>
        )}
      </div>

      {view.staleHeadline && (
        <div className="mt-3 rounded border border-amber-200 bg-amber-50 p-3">
          <p className="text-sm font-medium text-amber-800">{view.staleHeadline}</p>
          <ul className="mt-1 space-y-0.5">
            {view.staleNotices.map((note) => (
              <li key={note} className="text-xs text-amber-700">{note}</li>
            ))}
          </ul>
          <p className="text-xs text-amber-600 mt-2">
            Nothing was regenerated for you. These are flagged so you can decide what to revisit.
          </p>
        </div>
      )}
    </div>
  );
}

function AmendmentsPanel({ projectId }: { projectId: string }) {
  const canUpload = usePermission('upload');
  const canGenerate = usePermission('generate');
  const canApprove = usePermission('approve');

  const [expanded, setExpanded] = useState(true);
  const [versions, setVersions] = useState<ProtocolVersionLike[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [kind, setKind] = useState<ProtocolDocumentKind | null>(null);
  const [label, setLabel] = useState('');
  const [amendmentPath, setAmendmentPath] = useState('');
  const [busy, setBusy] = useState<'' | 'comparing' | 'adopting' | 'reanalysing'>('');
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');
  const [comparison, setComparison] = useState<ProtocolComparisonLike | null>(null);
  const [confirmingAdopt, setConfirmingAdopt] = useState(false);
  const [confirmingReanalyse, setConfirmingReanalyse] = useState(false);
  const [adopted, setAdopted] = useState<AdoptAmendmentResult | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const amendmentInputRef = useRef<HTMLInputElement>(null);

  /**
   * Reopen the most recent saved comparison, once, on arrival.
   *
   * The panel tells the user a comparison "can be reopened without running it
   * again". Until 2026-08-26 nothing read one back, so that sentence was false:
   * a reload discarded the result and the Adopt button with it, and the only
   * way forward was to pay for the model call a second time to recompute an
   * answer already sitting in Firestore.
   *
   * A one-shot read rather than a listener: this is a historical record that
   * only changes when the user runs a comparison from this very screen, and
   * that path already sets the state directly.
   *
   * `reopenedComparisonState` decides what it MEANS — an amendment that is
   * already the study's current version has been adopted, and re-offering
   * Adopt for it would misdescribe the study. Read-only and best-effort: a
   * study with no comparisons, or a failed read, simply shows the panel as it
   * always did.
   */
  useEffect(() => {
    if (!projectId) return;
    const unsubscribe = onSnapshot(
      query(protocolComparisonsCollection(projectId), orderBy('ranAt', 'desc'), limit(1)),
      (snapshot) => {
        if (snapshot.empty) return;
        // Normalised: the stored record spells the amendment path differently
        // from the callable's response, and the panel understands one of them.
        const latest = comparisonFromStoredRecord(
          { id: snapshot.docs[0].id, ...snapshot.docs[0].data() } as ProtocolComparisonLike,
        );
        if (!latest) return;
        // Never displace a comparison the user just ran on this screen.
        setComparison((existing) => existing ?? latest);
        // Adopt is gated on the UPLOAD FORM's state, which a reload empties. A
        // comparison restored without these reads as a finding whose one action
        // is refused — "Upload the amendment before adopting it." under an
        // amendment that is uploaded. Same guard: an in-progress upload wins.
        const form = reopenedFormState(latest);
        if (form.amendmentPath) setAmendmentPath((existing) => existing || form.amendmentPath);
        if (form.kind) setKind((existing) => existing ?? form.kind);
      },
      (err) => console.error('Could not reopen the saved comparison:', err),
    );
    return unsubscribe;
  }, [projectId]);

  // The history is Cloud-Function-written and member-readable; a listener keeps
  // the panel correct after an adoption without a refetch.
  useEffect(() => {
    if (!projectId) return;
    const unsubscribe = onSnapshot(
      protocolVersionsCollection(projectId),
      (snapshot) => {
        setVersions(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as ProtocolVersionLike));
      },
      (err) => console.error('[Amendments] could not read the version history:', err),
    );
    return () => unsubscribe();
  }, [projectId]);

  const history = useMemo(() => describeVersionHistory(versions), [versions]);
  const view = useMemo(
    () => (comparison ? describeComparison(comparison) : null),
    [comparison],
  );

  const kindError = kindSelectionError(kind);
  /**
   * A reopened comparison whose amendment is ALREADY the study's current
   * version has been adopted. Offering Adopt again would describe the study as
   * something it is not — and re-adoption is a no-op server-side, so the button
   * would do nothing while looking like it did something.
   *
   * Computed at render rather than at load, because the version history arrives
   * on its own listener and may not have landed when the comparison does.
   */
  const currentVersionPath = (versions.find((v) => v.current)?.storagePath ?? null) as
    | string
    | null;
  const alreadyAdopted =
    reopenedComparisonState(comparison, currentVersionPath) === 'already-adopted';

  const adoptBlocked = alreadyAdopted
    ? 'This amendment is already the version this study runs under. It was adopted ' +
      'earlier — the history below shows when.'
    : adoptionBlockedReason({
      canApprove,
      amendmentPath,
      kind,
    });

  /**
   * The label this version will actually carry.
   *
   * `protocolVersions.ts` takes the sponsor's label verbatim and falls back to
   * the date in UTC, `YYYY-MM-DD`. The same fallback is computed here so the
   * confirmation names the label that will be RECORDED rather than the
   * filename, which is not what a version is called.
   */
  const plannedLabel =
    label.trim() ||
    nextAvailableLabel(
      new Date().toISOString().slice(0, 10),
      versions.map((v) => String(v.label ?? '')),
    );

  const resetAmendment = () => {
    setSelectedFile(null);
    setKind(null);
    setLabel('');
    setAmendmentPath('');
    setComparison(null);
    setAdopted(null);
    setProgress('');
    setError('');
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    // A different file invalidates whatever was uploaded and compared before it.
    setAmendmentPath('');
    setComparison(null);
    setAdopted(null);
    setError('');
  };

  /**
   * Upload to the study's own protocol namespace and compare.
   *
   * The upload reuses `getUploadUrlCore` with `storagePath: 'protocols'`, the
   * same path `ProtocolUploader` takes, so the file lands inside
   * `projects/{projectId}/protocols/` with the validation everything else gets.
   * `compareProtocolsV2` re-checks that namespace server-side and refuses a path
   * outside it — this is convenience, not the boundary.
   */
  const runComparison = async () => {
    if (!selectedFile || !kind || !projectId) return;
    setBusy('comparing');
    setError('');
    setAdopted(null);
    try {
      let path = amendmentPath;

      if (!path) {
        setProgress('Uploading the amendment...');
        const idToken = await auth.currentUser?.getIdToken();
        if (!idToken) throw new Error('You must be signed in to upload an amendment.');

        const urlResponse = await fetch(
          `${import.meta.env.VITE_FIREBASE_FUNCTIONS_URL || ''}/getUploadUrlCore`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
            body: JSON.stringify({
              projectId,
              fileName: selectedFile.name,
              contentType: selectedFile.type || 'application/pdf',
              storagePath: 'protocols',
            }),
          },
        );
        if (!urlResponse.ok) {
          const serverError = await urlResponse.json().then((b) => b?.error).catch(() => null);
          throw new Error(serverError || `Failed to get upload URL (HTTP ${urlResponse.status})`);
        }
        const { uploadUrl, filePath } = await urlResponse.json();

        const putResponse = await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': selectedFile.type || 'application/pdf' },
          body: selectedFile,
        });
        if (!putResponse.ok) throw new Error('The amendment could not be uploaded.');

        path = filePath;
        setAmendmentPath(filePath);
      }

      setProgress('Reading the amendment. This takes a few minutes on a long document.');
      const compare = httpsCallable(functions, CLOUD_FUNCTIONS.COMPARE_PROTOCOLS, {
        timeout: CALLABLE_TIMEOUTS.MEDIUM,
      });
      const response = await compare({
        projectId,
        amendmentFilePath: path,
        documentKind: kind,
      });
      setComparison(response.data as ProtocolComparisonLike);
      setProgress('');
    } catch (err: unknown) {
      const e = err as { message?: string };
      setError(e.message || 'The amendment could not be compared.');
      setProgress('');
    } finally {
      setBusy('');
    }
  };

  const adopt = async () => {
    if (!projectId || !kind || !amendmentPath) return;
    setConfirmingAdopt(false);
    setBusy('adopting');
    setError('');
    try {
      const adoptFn = httpsCallable(functions, CLOUD_FUNCTIONS.ADOPT_AMENDMENT, {
        timeout: CALLABLE_TIMEOUTS.SHORT,
      });
      const response = await adoptFn({
        projectId,
        amendmentFilePath: amendmentPath,
        documentKind: kind,
        // Free text, taken verbatim. Blank means the server's own default —
        // today's date — which is what `plannedLabel` shows in the confirmation.
        label: label.trim() || null,
        // Recorded on the version so the adoption can say which comparison
        // informed it. Null when the comparison could not be saved — the server
        // treats an absent one as "adopted without a comparison", which is true.
        comparisonId: view?.reopenId ?? null,
      });
      setAdopted(response.data as AdoptAmendmentResult);
    } catch (err: unknown) {
      const e = err as { message?: string };
      setError(e.message || 'The amendment could not be adopted.');
    } finally {
      setBusy('');
    }
  };

  /**
   * The separate, explicit re-analysis. Never reached by adopting.
   *
   * It runs against the version the study is CURRENT on, which after an
   * adoption is the amendment — so this is how the study's arms, visits and name
   * are brought up to date, deliberately, by someone who read the warning.
   */
  const reanalyse = async () => {
    const storagePath = (versions.find((v) => v.current)?.storagePath ?? '') as string;
    if (!projectId || !storagePath) return;
    setConfirmingReanalyse(false);
    setBusy('reanalysing');
    setError('');
    try {
      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) throw new Error('You must be signed in to re-analyse the protocol.');
      const response = await fetch(
        `${import.meta.env.VITE_FIREBASE_FUNCTIONS_URL || ''}/analyzeProtocolV2`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
          body: JSON.stringify({ projectId, filePath: storagePath }),
        },
      );
      if (!response.ok) throw new Error('The protocol could not be re-analysed.');
      setProgress('The protocol has been re-analysed. Reload the page to see the new analysis.');
    } catch (err: unknown) {
      const e = err as { message?: string };
      setError(e.message || 'The protocol could not be re-analysed.');
    } finally {
      setBusy('');
    }
  };

  const downloadVersion = async (row: VersionView) => {
    if (!row.download.request) return;
    setDownloading(row.id);
    try {
      const fn = httpsCallable(functions, CLOUD_FUNCTIONS.GET_VERSION_DOWNLOAD_URL);
      const response = await fn({ projectId, ...row.download.request });
      const data = response.data as { url: string; filename: string | null };
      // The signed URL is cross-origin, where `download` is ignored — the
      // filename is signed into the URL server-side instead.
      const link = document.createElement('a');
      link.href = data.url;
      if (data.filename) link.download = data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err: unknown) {
      const e = err as { message?: string };
      setError(e.message || 'That version could not be downloaded.');
    } finally {
      setDownloading(null);
    }
  };

  const confirmation = adoptionConfirmation({
    kind: kind ?? 'full-revised',
    label: plannedLabel,
    previousLabel: history.current?.label ?? null,
    affectedArtifacts: view?.affectedArtifacts ?? [],
  });

  return (
    <div className="card p-6">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center space-x-3 text-left w-full -m-2 p-2 rounded hover:bg-gray-50"
        aria-expanded={expanded}
      >
        <div className="h-10 w-10 bg-sky-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <FileClock className="h-5 w-5 text-sky-600" />
        </div>
        <div className="flex-1">
          <div className="flex items-center space-x-2">
            <h3 className="font-semibold text-gray-900">Protocol Amendments</h3>
            <span className="text-xs text-gray-500">
              ({history.all.length} recorded version{history.all.length === 1 ? '' : 's'})
            </span>
            <ChevronDown
              className={`h-4 w-4 text-gray-400 transition-transform ${expanded ? '' : '-rotate-90'}`}
            />
          </div>
          <p className="text-sm text-gray-500">
            The protocol this study runs under, everything it has run under before, and how to
            bring an amendment in.
          </p>
        </div>
      </button>

      {expanded && (
        <div className="mt-5 space-y-6">
          {error && (
            <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* ── Current version and history ───────────────────────────── */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Current version</h4>
            {history.current ? (
              <VersionRow
                view={history.current}
                onDownload={downloadVersion}
                downloading={downloading}
              />
            ) : (
              <p className="text-sm text-gray-500 bg-gray-50 rounded px-4 py-3">
                {history.emptyNote}
              </p>
            )}
          </div>

          {history.superseded.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">
                Earlier versions ({history.superseded.length})
              </h4>
              <p className="text-xs text-gray-500 mb-2">
                Superseded versions are kept and stay downloadable. Nothing is deleted.
              </p>
              <div className="space-y-2">
                {history.superseded.map((row) => (
                  <VersionRow
                    key={row.id}
                    view={row}
                    onDownload={downloadVersion}
                    downloading={downloading}
                  />
                ))}
              </div>
            </div>
          )}

          {/* ── Re-analysis: separate, explicit, warned about ─────────── */}
          {history.current && (
            <div className="rounded-lg border border-gray-200 p-4">
              <h4 className="text-sm font-semibold text-gray-700">Bring the study design up to date</h4>
              <p className="text-sm text-gray-600 mt-1">
                Adopting a version does not re-read the protocol, so the study name, the study arms
                and the Schedule of Events still describe whatever was last analysed. Re-analysing is
                a separate act, and it replaces them.
              </p>
              <button
                type="button"
                onClick={() => setConfirmingReanalyse(true)}
                disabled={busy !== '' || !canGenerate}
                title={!canGenerate ? "Your role doesn't allow generation on this project." : undefined}
                className="mt-3 inline-flex items-center space-x-2 px-3 py-1.5 text-sm rounded-md border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {busy === 'reanalysing' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                <span>{REANALYSE_ACTION_LABEL}</span>
              </button>
            </div>
          )}

          {/* ── Bring an amendment in ─────────────────────────────────── */}
          <div className="rounded-lg border border-gray-200 p-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Upload an amendment</h4>

            {!selectedFile ? (
              <div
                onClick={() => canUpload && amendmentInputRef.current?.click()}
                title={!canUpload ? "Your role doesn't allow uploads on this project." : undefined}
                className={`border-2 border-dashed rounded-lg p-6 text-center ${
                  canUpload
                    ? 'border-gray-300 cursor-pointer hover:border-primary-400 hover:bg-primary-50'
                    : 'border-gray-200 opacity-60 cursor-not-allowed'
                } transition-colors`}
              >
                <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600">Click to select the amendment (PDF)</p>
                <input
                  ref={amendmentInputRef}
                  type="file"
                  accept=".pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
            ) : (
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <FileText className="h-8 w-8 text-primary-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{selectedFile.name}</p>
                  <p className="text-sm text-gray-500">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    {amendmentPath ? ' · uploaded' : ''}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={resetAmendment}
                  disabled={busy !== ''}
                  className="p-1 hover:bg-gray-200 rounded disabled:opacity-50"
                  aria-label="Remove this amendment"
                >
                  <X className="h-4 w-4 text-gray-500" />
                </button>
              </div>
            )}

            {/* THE KIND. Asked, never detected — and the panel will not send a
                comparison until one of the two is chosen. */}
            <fieldset className="mt-4">
              <legend className="text-sm font-semibold text-gray-800">
                {DOCUMENT_KIND_PROMPT} <span className="text-red-600">*</span>
              </legend>
              <p className="text-xs text-gray-600 mt-1">{DOCUMENT_KIND_WHY_ASKED}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                {DOCUMENT_KIND_CHOICES.map((choice) => {
                  const chosen = kind === choice.kind;
                  return (
                    <label
                      key={choice.kind}
                      className={`block rounded-lg border-2 p-4 cursor-pointer transition-colors ${
                        chosen
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-primary-300'
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        <input
                          type="radio"
                          name="amendment-document-kind"
                          value={choice.kind}
                          checked={chosen}
                          onChange={() => { setKind(choice.kind); setComparison(null); }}
                          className="mt-1 h-4 w-4 text-primary-600"
                        />
                        <div>
                          <p className="font-medium text-gray-900">{choice.title}</p>
                          <p className="text-sm text-gray-600 mt-0.5">{choice.oneLine}</p>
                          <p className="text-xs text-gray-500 mt-2">{choice.compareBehaviour}</p>
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
              {kindError && (
                <p className="text-xs text-red-600 mt-2 flex items-start space-x-1">
                  <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                  <span>{kindError}</span>
                </p>
              )}
            </fieldset>

            {/* The sponsor's own label, taken verbatim. There is no numbering
                scheme here on purpose: sponsors number protocols their own way,
                and an invented v1/v2/v3 would print a guess on a trial-folder
                record where it reads as a fact about the document. */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700" htmlFor="amendment-label">
                Version label <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                id="amendment-label"
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder={`e.g. Amendment 2 — blank records "${plannedLabel}"`}
                className="mt-1 w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                Whatever the sponsor calls this version. Left blank, it is recorded as the date it
                entered the platform.
              </p>
            </div>

            <div className="mt-4 flex items-center space-x-3">
              <button
                type="button"
                onClick={runComparison}
                disabled={busy !== '' || !selectedFile || !!kindError || !canGenerate}
                title={
                  !canGenerate
                    ? "Your role doesn't allow generation on this project."
                    : kindError || undefined
                }
                className="px-4 py-2 btn-primary inline-flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {busy === 'comparing' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <GitBranch className="h-4 w-4" />
                )}
                <span>{busy === 'comparing' ? 'Reading...' : 'Compare with the current version'}</span>
              </button>
              {progress && <span className="text-sm text-gray-600">{progress}</span>}
            </div>
          </div>

          {/* ── What the comparison found ─────────────────────────────── */}
          {view && (
            <div className="rounded-lg border border-gray-200 p-4 space-y-4">
              <div>
                <div className="flex items-center flex-wrap gap-2">
                  <h4 className="text-sm font-semibold text-gray-800">{view.headline}</h4>
                  <span className="text-xs text-gray-500">{view.modeLabel}</span>
                </div>
                {view.comparedAgainstLabel && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    Against version &quot;{view.comparedAgainstLabel}&quot;
                  </p>
                )}
                <p
                  className={`text-xs mt-2 ${view.saved ? 'text-gray-500' : 'text-amber-700 font-medium'}`}
                >
                  {view.savedNote}
                </p>
                {view.seededNote && (
                  <p className="text-xs text-gray-600 mt-1">{view.seededNote}</p>
                )}
              </div>

              {view.summary && (
                <p className="text-sm text-gray-700 bg-gray-50 rounded p-3">{view.summary}</p>
              )}

              <div>
                <p className="text-sm font-medium text-gray-700">{view.affectedArtifactsNote}</p>
              </div>

              {view.sections.length > 0 && (
                <div className="space-y-4">
                  {view.sections.map((group) => (
                    <div key={group.section}>
                      <h5 className="text-sm font-semibold text-gray-700 mb-1">
                        {group.section}{' '}
                        <span className="text-xs font-normal text-gray-400">
                          ({group.changes.length})
                        </span>
                      </h5>
                      <ul className="space-y-2">
                        {group.changes.map((change, i) => (
                          <li key={i} className="border border-gray-200 rounded p-3">
                            <div className="flex items-start space-x-2">
                              {change.changeType && (
                                <span className="px-2 py-0.5 rounded bg-sky-50 text-sky-700 text-xs font-medium flex-shrink-0">
                                  {change.changeType}
                                </span>
                              )}
                              <p className="text-sm text-gray-800 flex-1">{change.description}</p>
                            </div>
                            <p
                              className={`text-xs mt-1 ${
                                change.hasImpact ? 'text-gray-600' : 'text-gray-400 italic'
                              }`}
                            >
                              Impact: {change.impactNote}
                            </p>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}

              {/* ── Adopt ──────────────────────────────────────────────── */}
              <div className="pt-3 border-t border-gray-200">
                {adopted ? (
                  <p className="text-sm text-gray-600">
                    This amendment has been adopted — see below.
                  </p>
                ) : adoptBlocked ? (
                  <p className="text-sm text-gray-600 bg-gray-50 rounded p-3">{adoptBlocked}</p>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmingAdopt(true)}
                    disabled={busy !== ''}
                    className="px-4 py-2 btn-primary inline-flex items-center space-x-2 disabled:opacity-50"
                  >
                    {busy === 'adopting' ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4" />
                    )}
                    <span>Adopt this amendment</span>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ── What the adoption did ─────────────────────────────────── */}
          {adopted && (
            <div className="rounded-lg border border-green-200 bg-green-50 p-4">
              <div className="flex items-start space-x-3">
                <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-green-800">
                    {adopted.status === 'unchanged'
                      ? `"${adopted.label}" was already this study's current version, so nothing changed.`
                      : `This study now runs under "${adopted.label}".`}
                  </p>
                  <ul className="mt-2 space-y-1 text-sm text-green-700">
                    {adopted.supersededVersion && (
                      <li>
                        &quot;{adopted.supersededVersion.label}&quot; is archived. It is still listed
                        above and still downloadable.
                      </li>
                    )}
                    <li>
                      {adopted.filedInTmf
                        ? 'The document is filed in the trial folder under Signed Protocol and Amendments.'
                        : 'The document could not be filed in the trial folder. Nothing else was affected, and the version record stands.'}
                    </li>
                    <li>
                      {adopted.affectedArtifacts.length > 0
                        ? `${adopted.affectedArtifacts.length} artifact${
                            adopted.affectedArtifacts.length === 1 ? ' is' : 's are'
                          } marked as possibly out of date: ${adopted.affectedArtifacts.join(', ')}. Nothing was regenerated.`
                        : 'No artifacts were flagged, and nothing was regenerated.'}
                    </li>
                    <li>
                      The protocol was not re-analysed, so the study name, the study arms and the
                      Schedule of Events are unchanged. Re-analysing is the separate action above.
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── The adoption confirmation ───────────────────────────────── */}
      {confirmingAdopt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-elevated max-w-lg w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900">{confirmation.title}</h3>

            <div className="mt-4">
              <p className="text-sm font-semibold text-gray-700">What this does</p>
              <ul className="mt-1 space-y-1">
                {confirmation.willHappen.map((line) => (
                  <li key={line} className="text-sm text-gray-700 flex items-start space-x-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-4">
              <p className="text-sm font-semibold text-gray-700">What this does not do</p>
              <ul className="mt-1 space-y-1">
                {confirmation.willNotHappen.map((line) => (
                  <li key={line} className="text-sm text-gray-700 flex items-start space-x-2">
                    <Circle className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-6 flex space-x-3">
              <button
                type="button"
                onClick={() => setConfirmingAdopt(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                {confirmation.cancelLabel}
              </button>
              <button
                type="button"
                onClick={adopt}
                className="flex-1 px-4 py-2 btn-primary"
              >
                {confirmation.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── The re-analysis confirmation ────────────────────────────── */}
      {confirmingReanalyse && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-elevated max-w-lg w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900">{REANALYSE_ACTION_LABEL}?</h3>
            <div className="mt-3 flex items-start space-x-2 rounded border border-amber-200 bg-amber-50 p-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">
                {reanalysisWarning(history.current?.label)}
              </p>
            </div>
            <div className="mt-6 flex space-x-3">
              <button
                type="button"
                onClick={() => setConfirmingReanalyse(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={reanalyse}
                className="flex-1 px-4 py-2 rounded-lg bg-amber-600 text-white hover:bg-amber-700"
              >
                {REANALYSE_ACTION_LABEL}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ProtocolIntelligence() {
  const { projectId } = useParams<{ projectId: string }>();
  const canUpload = usePermission('upload');
  const canGenerate = usePermission('generate');
  const [showUploader, setShowUploader] = useState(false);
  const [analysis, setAnalysis] = useState<ProtocolAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [showVerificationNotes, setShowVerificationNotes] = useState(false);
  const [project, setProject] = useState<any>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [requirements, setRequirements] = useState<ProtocolRequirement[]>([]);
  const [extractingRequirements, setExtractingRequirements] = useState(false);
  const [extractRequirementsError, setExtractRequirementsError] = useState<string | null>(null);
  const [showSupersededRequirements, setShowSupersededRequirements] = useState(false);
  const [requirementsExpanded, setRequirementsExpanded] = useState(true);
  // Surfaced when the backend writes status: "extraction_failed" to
  // protocolAnalysis/latest. The doc carries error.code (e.g. LLM_TIMEOUT)
  // and error.stage so the user can see what went wrong before retrying.
  const [extractionError, setExtractionError] = useState<
    { code: string; message: string; stage?: string } | null
  >(null);

  useEffect(() => {
    if (!projectId) return;

    // Load project data
    const loadProject = async () => {
      const projectDoc = await getDoc(projectDocRef(projectId));
      if (projectDoc.exists()) {
        setProject(projectDoc.data());
      }
    };
    loadProject();

    // Subscribe to protocol analysis
    const unsubscribe = onSnapshot(
      protocolAnalysisLatestDoc(projectId),
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          // Failure marker shape: { status: "extraction_failed", error: {...} }
          // (no `analysis` field). Surface this to the user instead of
          // rendering an empty cards view that looks like a sparse success.
          if (data.status === 'extraction_failed') {
            const err = (data.error || {}) as { code?: string; message?: string; stage?: string };
            setExtractionError({
              code: err.code || 'EXTRACTION_FAILED',
              message: err.message || 'The protocol could not be read.',
              stage: err.stage,
            });
            setAnalysis(null);
            setRequirements([]);
          } else {
            setExtractionError(null);
            setAnalysis(data.analysis || data);
            // Phase 1: requirements live as a top-level field on the same doc.
            // May be missing on projects whose analysis predates the feature.
            const reqs = Array.isArray(data.requirements) ? (data.requirements as ProtocolRequirement[]) : [];
            setRequirements(reqs);
          }
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error loading analysis:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [projectId]);

  // Group active requirements by type for display in the Requirements panel.
  // Superseded requirements are tracked separately so they can be hidden by default.
  const groupedRequirements = useMemo<RequirementGroup[]>(() => {
    const byType = new Map<ProtocolRequirementType, ProtocolRequirement[]>();
    for (const req of requirements) {
      if (req.status !== 'active') continue;
      const list = byType.get(req.type) ?? [];
      list.push(req);
      byType.set(req.type, list);
    }
    return REQUIREMENT_GROUP_ORDER
      .filter((g) => (byType.get(g.type)?.length ?? 0) > 0)
      .map((g) => ({ type: g.type, label: g.label, items: byType.get(g.type) ?? [] }));
  }, [requirements]);

  const supersededRequirements = useMemo(
    () => requirements.filter((r) => r.status === 'superseded'),
    [requirements]
  );

  const activeRequirementCount = useMemo(
    () => requirements.filter((r) => r.status === 'active').length,
    [requirements]
  );

  const handleExtractRequirements = async () => {
    if (!projectId || extractingRequirements) return;
    setExtractingRequirements(true);
    setExtractRequirementsError(null);
    try {
      const fn = httpsCallable(functions, CLOUD_FUNCTIONS.EXTRACT_REQUIREMENTS);
      const result = await fn({ projectId });
      console.log('[ExtractRequirements] result:', result.data);
      // The onSnapshot listener picks up the new requirements automatically.
    } catch (err: any) {
      console.error('[ExtractRequirements] failed:', err);
      setExtractRequirementsError(err?.message || 'Failed to extract requirements');
    } finally {
      setExtractingRequirements(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'];
    if (file && (allowedTypes.includes(file.type) || file.name.match(/\.(pdf|docx|doc)$/i))) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !projectId) return;

    setUploading(true);
    setUploadProgress('Getting upload URL...');

    try {
      // Get an authenticated upload URL. getUploadUrl requires a Firebase ID
      // token and the `upload` privilege on the project — added when uploads
      // were hardened to per-project, privilege-gated storage. Without the
      // Authorization header the endpoint returns 401 and the upload can't
      // start ("Failed to get upload URL").
      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) throw new Error('You must be signed in to upload a protocol.');

      const getUploadUrlResponse = await fetch(
        `${import.meta.env.VITE_FIREBASE_FUNCTIONS_URL || ''}/getUploadUrlCore`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
          body: JSON.stringify({
            fileName: selectedFile.name,
            contentType: selectedFile.type,
            storagePath: 'protocols',
            projectId
          })
        }
      );

      if (!getUploadUrlResponse.ok) {
        // Surface the server's actual error instead of masking every non-2xx as
        // the same generic string.
        const serverError = await getUploadUrlResponse.json().then(b => b?.error).catch(() => null);
        throw new Error(serverError || `Failed to get upload URL (HTTP ${getUploadUrlResponse.status})`);
      }
      const { uploadUrl, filePath } = await getUploadUrlResponse.json();

      // Upload file
      setUploadProgress('Uploading protocol...');
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': selectedFile.type },
        body: selectedFile
      });

      if (!uploadResponse.ok) throw new Error('Failed to upload file');

      // Declare WHICH file is this study's protocol, before analysing it.
      //
      // Until 2026-08-26 only the Dashboard creation wizard did this, so a
      // protocol uploaded into an EXISTING study left the field unset and
      // everything downstream fell back to guessing from Storage:
      // `chooseProtocolObject` ranks this as the strongest signal — the
      // study's own declaration — and `seedInitialVersion` uses the same
      // ladder to give a study its version 1, which the amendment flow is
      // built on. With one PDF the fallback picks the only candidate and
      // nothing looks wrong; with two it guesses, and an amendment IS the
      // second one. This page also keys its own status line on the field, so
      // it read "Analysis available" instead of "Protocol uploaded and
      // analyzed" right where the upload had just happened.
      //
      // Sandboxed: the analysis below is the point of this flow, and a failure
      // to record the declaration must not cost the user the analysis. The
      // fallback still works — it is just weaker.
      try {
        await updateDoc(projectDocRef(projectId), {
          protocolFilePath: filePath,
          updatedAt: serverTimestamp(),
        });
      } catch (stampErr) {
        console.error('Failed to record protocolFilePath:', stampErr);
      }

      // Analyze protocol (reuses the ID token obtained above).
      setUploadProgress('Analyzing protocol...');
      const analyzeResponse = await fetch(
        `${import.meta.env.VITE_FIREBASE_FUNCTIONS_URL || ''}/analyzeProtocolV2`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
          body: JSON.stringify({ projectId, filePath })
        }
      );

      if (!analyzeResponse.ok) throw new Error('Failed to analyze protocol');

      // Generate ALL documents from protocol
      setUploadProgress('Generating study design...');

      try {
        const generateAllResponse = await fetch(
          `${import.meta.env.VITE_FIREBASE_FUNCTIONS_URL || ''}/generateAllFromProtocolV2`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
            body: JSON.stringify({ projectId })
          }
        );

        if (generateAllResponse.ok) {
          const result = await generateAllResponse.json();
          console.log('Generated all documents:', result);
          setUploadProgress(`Generated ${result.generated?.crfs || 0} CRFs, data dictionary, edit checks, and DB schema!`);
        } else {
          // Fall back to individual generation.
          //
          // NOTE: We intentionally do NOT call generateArtifactDemo with
          // artifactType: 'crf' in the fallback. That path uses the legacy
          // CRFGenerator which seeds ~8 skeleton forms from the visit
          // schedule. Those seeds open the Builder in 'workspace' phase with
          // 8 forms and hide the Generate button that runs `suggestCRFForms`
          // — the LLM-based pass that produces 40–50 context-aware forms.
          // Same rationale as `generateAllFromProtocol.ts:128–137` and
          // `ProtocolUploader.tsx`.
          setUploadProgress('Generating consent form...');
          await fetch(
            `${import.meta.env.VITE_FIREBASE_FUNCTIONS_URL || ''}/generateArtifactDemoV2`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
              body: JSON.stringify({ projectId, artifactType: 'consent_form' })
            }
          );
        }
      } catch (genError) {
        console.error('Generation error, falling back:', genError);
        // Fall back to basic generation
        await fetch(
          `${import.meta.env.VITE_FIREBASE_FUNCTIONS_URL || ''}/generateArtifactDemoV2`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
            body: JSON.stringify({ projectId, artifactType: 'consent_form' })
          }
        );
      }

      setUploadProgress('Complete!');
      setShowUploader(false);
      setSelectedFile(null);
    } catch (error) {
      console.error('Upload error:', error);
      setUploadProgress('Error: ' + (error as Error).message);
    } finally {
      setUploading(false);
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  // No analysis yet - show upload prompt (with extraction-failure banner if applicable)
  if (!analysis) {
    return (
      <div className="mx-auto max-w-5xl space-y-6">
        {extractionError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
            <AlertTriangle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-red-800">The protocol could not be read</p>
              <p className="text-sm text-red-700 mt-1">{extractionError.message}</p>
              <p className="text-xs text-red-600 mt-2 font-mono">
                code: {extractionError.code}
                {extractionError.stage ? ` · stage: ${extractionError.stage}` : ''}
              </p>
              <p className="text-sm text-red-700 mt-2">
                You can retry by uploading the protocol again below. If it keeps failing, the document
                may be too large or the reading service may be temporarily unavailable.
              </p>
            </div>
          </div>
        )}
        {/* Upload Section */}
        <div className="overflow-hidden rounded-[22px] border border-[#DCE2E5] bg-white shadow-[0_18px_48px_rgba(15,42,50,0.08)]">
          <div className="border-b border-[#EDF0F1] bg-gradient-to-r from-[#EAF6F4] via-white to-[#FFF6E7] px-8 py-10 text-center sm:px-12">
          <div className="mb-6">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#001820] shadow-[0_8px_20px_rgba(0,24,32,0.16)]">
              <Upload className="h-8 w-8 text-white" />
            </div>
          </div>
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.16em] text-[var(--brand-primary)]">Protocol intelligence</p>
          <h2 className="mb-3 text-3xl font-normal tracking-[-0.035em] text-[#001820]">
            Upload Your Protocol
          </h2>
          <p className="mx-auto mb-7 max-w-xl text-[15px] leading-6 text-[#5F696C]">
            Upload your clinical trial protocol (PDF or Word) to begin. The system will analyze it
            and extract key study parameters, endpoints, and requirements.
          </p>
          <button
            onClick={() => setShowUploader(true)}
            disabled={!canUpload}
            title={!canUpload ? "Your role doesn't allow uploads on this project." : undefined}
            className="inline-flex items-center space-x-2 rounded-full bg-[#001820] px-6 py-3.5 text-sm font-bold text-white shadow-[0_8px_20px_rgba(0,24,32,0.16)] transition hover:bg-[#00313A] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Upload className="h-5 w-5" />
            <span>Upload Protocol</span>
          </button>

          </div>

          <div className="px-6 py-8 sm:px-10">
            <p className="mb-6 text-center text-xs font-bold uppercase tracking-[0.14em] text-[#5F696C]">What happens after upload</p>
            <div className="grid grid-cols-2 gap-5 sm:grid-cols-7 sm:items-center sm:gap-3">
              <div className="text-center">
                <div className="mx-auto mb-2 flex h-11 w-11 items-center justify-center rounded-xl bg-[#E6F4F2]">
                  <FileText className="h-5 w-5 text-[var(--brand-primary)]" />
                </div>
                <p className="text-xs text-gray-600">Parse Protocol</p>
              </div>
              <ArrowRight className="mx-auto hidden h-5 w-5 text-[#AAB4B7] sm:block" />
              <div className="text-center">
                <div className="mx-auto mb-2 flex h-11 w-11 items-center justify-center rounded-xl bg-[#E6F4F2]">
                  <Beaker className="h-5 w-5 text-[var(--brand-primary)]" />
                </div>
                <p className="text-xs text-gray-600">Analysis</p>
              </div>
              <ArrowRight className="mx-auto hidden h-5 w-5 text-[#AAB4B7] sm:block" />
              <div className="text-center">
                <div className="mx-auto mb-2 flex h-11 w-11 items-center justify-center rounded-xl bg-[#FFF4DF]">
                  <Target className="h-5 w-5 text-[#B46B08]" />
                </div>
                <p className="text-xs text-gray-600">Extract Elements</p>
              </div>
              <ArrowRight className="mx-auto hidden h-5 w-5 text-[#AAB4B7] sm:block" />
              <div className="text-center">
                <div className="mx-auto mb-2 flex h-11 w-11 items-center justify-center rounded-xl bg-[#E9F6EF]">
                  <CheckCircle2 className="h-5 w-5 text-[#16784B]" />
                </div>
                <p className="text-xs text-gray-600">Generate Artifacts</p>
              </div>
            </div>
          </div>
        </div>

        {/* Upload Modal */}
        {showUploader && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-elevated max-w-md w-full mx-4 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Upload Protocol</h3>
                <button
                  onClick={() => { setShowUploader(false); setSelectedFile(null); }}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              {!selectedFile ? (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-primary-400 hover:bg-primary-50 transition-colors"
                >
                  <Upload className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">Click to select a PDF or Word file</p>
                  <p className="text-xs text-gray-500 mt-1">or drag and drop</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.docx,.doc"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    <FileText className="h-8 w-8 text-primary-600" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{selectedFile.name}</p>
                      <p className="text-sm text-gray-500">
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <button
                      onClick={() => setSelectedFile(null)}
                      className="p-1 hover:bg-gray-200 rounded"
                    >
                      <X className="h-4 w-4 text-gray-500" />
                    </button>
                  </div>

                  {uploading && (
                    <div className="p-3 bg-primary-50 rounded-lg">
                      <StepProgress
                        active={uploading}
                        steps={[
                          { label: 'Uploading protocol document...', delay: 0 },
                          { label: 'Analyzing protocol...', delay: 3000 },
                          { label: 'Extracting study design and objectives...', delay: 10000 },
                          { label: 'Identifying endpoints and procedures...', delay: 20000 },
                          { label: 'Generating study artifacts...', delay: 35000 },
                          { label: 'Building visit schedule and CRFs...', delay: 50000 },
                        ]}
                      />
                    </div>
                  )}
                  {!uploading && uploadProgress && (
                    <div className="flex items-center space-x-2 p-3 bg-green-50 rounded-lg">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      <span className="text-sm text-green-700">{uploadProgress}</span>
                    </div>
                  )}

                  <div className="flex space-x-3">
                    <button
                      onClick={() => { setShowUploader(false); setSelectedFile(null); }}
                      disabled={uploading}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleUpload}
                      disabled={uploading || !canUpload}
                      title={!canUpload ? "Your role doesn't allow uploads on this project." : undefined}
                      className="flex-1 px-4 py-2 btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                    >
                      {uploading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Upload className="h-4 w-4" />
                      )}
                      <span>{uploading ? 'Processing...' : 'Upload & Analyze'}</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Analysis exists - show results
  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Status Banner */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <CheckCircle2 className="h-6 w-6 text-green-600" />
          <div>
            <p className="font-medium text-green-800">Protocol Analysis Complete</p>
            <p className="text-sm text-green-600">
              {project?.protocolFilePath ? 'Protocol uploaded and analyzed' : 'Analysis available'}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {/* Extraction Confidence Badge */}
          {analysis?.extractionMetadata && (
            <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
              analysis.extractionMetadata.completenessConfidence === 'high'
                ? 'bg-green-200 text-green-800'
                : analysis.extractionMetadata.completenessConfidence === 'medium'
                ? 'bg-yellow-200 text-yellow-800'
                : 'bg-red-200 text-red-800'
            }`}>
              {analysis.extractionMetadata.completenessConfidence === 'high' ? 'Verified Complete' :
               analysis.extractionMetadata.completenessConfidence === 'medium' ? 'Partially Verified' :
               'Needs Review'}
              {(analysis.extractionMetadata.itemsAddedInVerification ?? 0) > 0 && (
                <span className="ml-1">
                  (+{analysis.extractionMetadata.itemsAddedInVerification} items added in verification)
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Extraction Verification Notes (collapsible) */}
      {(analysis?.extractionMetadata?.verificationNotes?.length ?? 0) > 0 &&
       analysis?.extractionMetadata?.verificationNotes?.[0] !== 'Extraction is complete' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg">
          <button
            onClick={() => setShowVerificationNotes(!showVerificationNotes)}
            className="w-full p-4 flex items-center justify-between text-left"
          >
            <div className="flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
              <div>
                <p className="font-medium text-amber-800 text-sm">
                  Verification Notes ({analysis?.extractionMetadata?.passesRun ?? 1}-pass extraction)
                </p>
                <p className="text-xs text-amber-600">
                  {analysis?.extractionMetadata?.totalItemsExtracted ?? 0} total items extracted
                  {(analysis?.extractionMetadata?.itemsAddedInVerification ?? 0) > 0 &&
                    ` (${analysis?.extractionMetadata?.itemsAddedInVerification} added during verification pass)`}
                </p>
              </div>
            </div>
            <ChevronDown className={`h-4 w-4 text-amber-600 transition-transform ${showVerificationNotes ? 'rotate-180' : ''}`} />
          </button>
          {showVerificationNotes && (
            <div className="px-4 pb-4 pl-11 space-y-1">
              {analysis?.extractionMetadata?.verificationNotes?.map((note: string, i: number) => (
                <p key={i} className="text-xs text-amber-700">{note}</p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Analysis Summary Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Study Design Card */}
        <div className="card p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Beaker className="h-5 w-5 text-blue-600" />
            </div>
            <h3 className="font-semibold text-gray-900">Study Design</h3>
          </div>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-gray-500">Type</dt>
              <dd className="font-medium text-gray-900">
                {analysis.studyDesign?.type || 'Not specified'}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Phase</dt>
              <dd className="font-medium text-gray-900">
                {analysis.studyDesign?.phase || 'Not specified'}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Blinding</dt>
              <dd className="font-medium text-gray-900">
                {analysis.studyDesign?.blinding || 'Not specified'}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Randomization</dt>
              <dd className="font-medium text-gray-900">
                {analysis.studyDesign?.randomization || 'Not specified'}
              </dd>
            </div>
            {analysis.studyDesign?.designFeatures && analysis.studyDesign.designFeatures.length > 0 && (
              <div className="flex justify-between">
                <dt className="text-gray-500">Design Features</dt>
                <dd className="font-medium text-gray-900">
                  {analysis.studyDesign.designFeatures.join(', ')}
                </dd>
              </div>
            )}
          </dl>
        </div>

        {/* Population Card */}
        <div className="card p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="h-10 w-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Users className="h-5 w-5 text-purple-600" />
            </div>
            <h3 className="font-semibold text-gray-900">Population</h3>
          </div>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-gray-500">Target Size</dt>
              <dd className="font-medium text-gray-900">
                {analysis.population?.targetEnrollment || analysis.population?.targetSize || 'TBD'} subjects
              </dd>
            </div>
            {analysis.population?.ageRange && analysis.population.ageRange !== 'Not specified' && (
              <div className="flex justify-between">
                <dt className="text-gray-500">Age Range</dt>
                <dd className="font-medium text-gray-900">{analysis.population.ageRange}</dd>
              </div>
            )}
            {analysis.population?.keyCondition && analysis.population.keyCondition !== 'Not specified' && (
              <div className="flex justify-between">
                <dt className="text-gray-500">Condition</dt>
                <dd className="font-medium text-gray-900">{analysis.population.keyCondition}</dd>
              </div>
            )}
            <div>
              {(analysis.population?.extractionStatus?.inclusion || analysis.population?.extractionStatus?.exclusion) && (
                <p className="text-sm text-amber-700 mb-2">Eligibility criteria could not be read automatically from this protocol; please add them.</p>
              )}
              <dt className="text-gray-500 mb-1">Inclusion Criteria ({analysis.population?.inclusion?.length || 0})</dt>
              <dd className="text-gray-900 max-h-32 overflow-y-auto">
                {analysis.population?.inclusion?.map((item: string, i: number) => (
                  <div key={i} className="text-xs bg-green-50 rounded px-2 py-1 mb-1">
                    {item}
                  </div>
                )) || <span className="text-gray-400">Not specified</span>}
              </dd>
            </div>
            {(analysis.population?.exclusion?.length ?? 0) > 0 && (
              <div>
                <dt className="text-gray-500 mb-1">Exclusion Criteria ({analysis.population?.exclusion?.length ?? 0})</dt>
                <dd className="text-gray-900 max-h-32 overflow-y-auto">
                  {analysis.population?.exclusion?.map((item: string, i: number) => (
                    <div key={i} className="text-xs bg-red-50 rounded px-2 py-1 mb-1">
                      {item}
                    </div>
                  ))}
                </dd>
              </div>
            )}
          </dl>
        </div>

        {/* Schedule Card */}
        <div className="card p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="h-10 w-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Calendar className="h-5 w-5 text-amber-600" />
            </div>
            <h3 className="font-semibold text-gray-900">Schedule</h3>
          </div>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-gray-500">Duration</dt>
              <dd className="font-medium text-gray-900">
                {analysis.schedule?.studyDuration || analysis.schedule?.duration || 'Not specified'}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Total Visits</dt>
              <dd className="font-medium text-gray-900">
                {analysis.schedule?.totalVisits || analysis.schedule?.visits?.length || 0} visits
              </dd>
            </div>
            {(analysis.schedule?.visits?.length ?? 0) > 0 && (
              <div>
                <dt className="text-gray-500 mb-1">Schedule of Events</dt>
                <dd className="text-gray-900 max-h-40 overflow-y-auto">
                  {analysis.schedule?.visits?.map((v: any, i: number) => (
                    <div key={i} className="text-xs bg-amber-50 rounded px-2 py-1 mb-1 flex justify-between">
                      <span className="font-medium">{v.name}</span>
                      <span className="text-gray-500">{v.timing}</span>
                    </div>
                  ))}
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>

      {/* Interventions & Arms */}
      {((analysis.interventions?.length ?? 0) > 0 || (analysis.arms?.length ?? 0) > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {(analysis.interventions?.length ?? 0) > 0 && (
            <div className="card p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="h-10 w-10 bg-teal-100 rounded-lg flex items-center justify-center">
                  <Beaker className="h-5 w-5 text-teal-600" />
                </div>
                <h3 className="font-semibold text-gray-900">Interventions</h3>
              </div>
              <div className="space-y-3">
                {analysis.interventions!.map((drug: any, i: number) => (
                  <div key={i} className="bg-teal-50 rounded-lg p-3">
                    <div className="font-medium text-sm text-gray-900">{drug.name}</div>
                    <div className="text-xs text-gray-600 mt-1 space-y-0.5">
                      {drug.dose && drug.dose !== 'Not specified' && <div>Dose: {drug.dose}</div>}
                      {drug.route && drug.route !== 'Not specified' && <div>Route: {drug.route}</div>}
                      {drug.frequency && drug.frequency !== 'Not specified' && <div>Frequency: {drug.frequency}</div>}
                      {drug.duration && drug.duration !== 'Not specified' && <div>Duration: {drug.duration}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(analysis.arms?.length ?? 0) > 0 && (
            <div className="card p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="h-10 w-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <GitBranch className="h-5 w-5 text-indigo-600" />
                </div>
                <h3 className="font-semibold text-gray-900">Study Arms ({analysis.arms!.length})</h3>
              </div>
              <div className="space-y-2">
                {analysis.arms!.map((arm: any, i: number) => (
                  <div key={i} className="bg-indigo-50 rounded-lg p-3">
                    <div className="font-medium text-sm text-gray-900">{arm.name}</div>
                    <div className="text-xs text-gray-600 mt-1">{arm.description}</div>
                    {arm.ratio && <div className="text-xs text-indigo-600 mt-1">Ratio: {arm.ratio}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Lab Tests, Procedures, Questionnaires */}
      {((analysis.labTests?.length ?? 0) > 0 || (analysis.procedures?.length ?? 0) > 0 || (analysis.questionnaires?.length ?? 0) > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {(analysis.labTests?.length ?? 0) > 0 && (
            <div className="card p-6">
              <h3 className="font-semibold text-gray-900 mb-3">Lab Tests ({analysis.labTests!.length})</h3>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {analysis.labTests!.map((lab: string, i: number) => (
                  <div key={i} className="text-xs bg-blue-50 text-gray-700 rounded px-2 py-1">{lab}</div>
                ))}
              </div>
            </div>
          )}
          {(analysis.procedures?.length ?? 0) > 0 && (
            <div className="card p-6">
              <h3 className="font-semibold text-gray-900 mb-3">Procedures ({analysis.procedures!.length})</h3>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {analysis.procedures!.map((proc: string, i: number) => (
                  <div key={i} className="text-xs bg-green-50 text-gray-700 rounded px-2 py-1">{proc}</div>
                ))}
              </div>
            </div>
          )}
          {(analysis.questionnaires?.length ?? 0) > 0 && (
            <div className="card p-6">
              <h3 className="font-semibold text-gray-900 mb-3">Questionnaires ({analysis.questionnaires!.length})</h3>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {analysis.questionnaires!.map((q: string, i: number) => (
                  <div key={i} className="text-xs bg-purple-50 text-gray-700 rounded px-2 py-1">{q}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Extended Protocol Fields */}
      {((analysis.concomitantMedications?.prohibited?.length ?? 0) > 0 ||
        (analysis.dataCollection?.biomarkers?.length ?? 0) > 0 ||
        (analysis.dataCollection?.imagingStudies?.length ?? 0) > 0 ||
        (analysis.stratificationFactors?.length ?? 0) > 0 ||
        (analysis.withdrawalCriteria?.length ?? 0) > 0 ||
        (analysis.dosageModifications?.length ?? 0) > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {(analysis.concomitantMedications?.prohibited?.length ?? 0) > 0 && (
            <div className="card p-6">
              <h3 className="font-semibold text-gray-900 mb-3">Concomitant Medications</h3>
              {(analysis.concomitantMedications?.prohibited?.length ?? 0) > 0 && (
                <div className="mb-2">
                  <p className="text-xs font-medium text-red-600 mb-1">Prohibited</p>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {analysis.concomitantMedications?.prohibited?.map((med: string, i: number) => (
                      <div key={i} className="text-xs bg-red-50 text-gray-700 rounded px-2 py-1">{med}</div>
                    ))}
                  </div>
                </div>
              )}
              {(analysis.concomitantMedications?.requiredBackground?.length ?? 0) > 0 && (
                <div>
                  <p className="text-xs font-medium text-green-600 mb-1">Required Background</p>
                  <div className="space-y-1 max-h-24 overflow-y-auto">
                    {analysis.concomitantMedications?.requiredBackground?.map((med: string, i: number) => (
                      <div key={i} className="text-xs bg-green-50 text-gray-700 rounded px-2 py-1">{med}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {((analysis.dataCollection?.biomarkers?.length ?? 0) > 0 || (analysis.dataCollection?.imagingStudies?.length ?? 0) > 0) && (
            <div className="card p-6">
              <h3 className="font-semibold text-gray-900 mb-3">Data Collection</h3>
              {(analysis.dataCollection?.biomarkers?.length ?? 0) > 0 && (
                <div className="mb-2">
                  <p className="text-xs font-medium text-violet-600 mb-1">Biomarkers ({analysis.dataCollection?.biomarkers?.length ?? 0})</p>
                  <div className="space-y-1 max-h-24 overflow-y-auto">
                    {analysis.dataCollection?.biomarkers?.map((b: string, i: number) => (
                      <div key={i} className="text-xs bg-violet-50 text-gray-700 rounded px-2 py-1">{b}</div>
                    ))}
                  </div>
                </div>
              )}
              {(analysis.dataCollection?.imagingStudies?.length ?? 0) > 0 && (
                <div>
                  <p className="text-xs font-medium text-cyan-600 mb-1">Imaging ({analysis.dataCollection?.imagingStudies?.length ?? 0})</p>
                  <div className="space-y-1 max-h-24 overflow-y-auto">
                    {analysis.dataCollection?.imagingStudies?.map((img: string, i: number) => (
                      <div key={i} className="text-xs bg-cyan-50 text-gray-700 rounded px-2 py-1">{img}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {((analysis.stratificationFactors?.length ?? 0) > 0 ||
            (analysis.withdrawalCriteria?.length ?? 0) > 0 ||
            (analysis.dosageModifications?.length ?? 0) > 0) && (
            <div className="card p-6">
              <h3 className="font-semibold text-gray-900 mb-3">Study Rules</h3>
              {(analysis.stratificationFactors?.length ?? 0) > 0 && (
                <div className="mb-2">
                  <p className="text-xs font-medium text-gray-600 mb-1">Stratification Factors</p>
                  <div className="space-y-1">
                    {analysis.stratificationFactors?.map((f: string, i: number) => (
                      <div key={i} className="text-xs bg-gray-50 text-gray-700 rounded px-2 py-1">{f}</div>
                    ))}
                  </div>
                </div>
              )}
              {(analysis.withdrawalCriteria?.length ?? 0) > 0 && (
                <div className="mb-2">
                  <p className="text-xs font-medium text-orange-600 mb-1">Withdrawal Criteria</p>
                  <div className="space-y-1 max-h-24 overflow-y-auto">
                    {analysis.withdrawalCriteria?.map((c: string, i: number) => (
                      <div key={i} className="text-xs bg-orange-50 text-gray-700 rounded px-2 py-1">{c}</div>
                    ))}
                  </div>
                </div>
              )}
              {(analysis.dosageModifications?.length ?? 0) > 0 && (
                <div>
                  <p className="text-xs font-medium text-blue-600 mb-1">Dose Modifications</p>
                  <div className="space-y-1 max-h-24 overflow-y-auto">
                    {analysis.dosageModifications?.map((d: string, i: number) => (
                      <div key={i} className="text-xs bg-blue-50 text-gray-700 rounded px-2 py-1">{d}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Objectives & Endpoints */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Objectives */}
        <div className="card p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Target className="h-5 w-5 text-green-600" />
            </div>
            <h3 className="font-semibold text-gray-900">Study Objectives</h3>
          </div>

          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Primary</h4>
              {analysis.objectives?.primary?.map((obj, i) => (
                <div key={i} className="flex items-start space-x-2 mb-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-600">{obj}</p>
                </div>
              )) || <p className="text-sm text-gray-500">Not specified</p>}
            </div>

            {analysis.objectives?.secondary && analysis.objectives.secondary.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Secondary</h4>
                {analysis.objectives.secondary.slice(0, 3).map((obj, i) => (
                  <div key={i} className="flex items-start space-x-2 mb-2">
                    <Circle className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-gray-600">{obj}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Endpoints */}
        <div className="card p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="h-10 w-10 bg-red-100 rounded-lg flex items-center justify-center">
              <Activity className="h-5 w-5 text-red-600" />
            </div>
            <h3 className="font-semibold text-gray-900">Study Endpoints</h3>
          </div>

          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Primary</h4>
              {analysis.endpoints?.primary?.map((endpoint, i) => (
                <div key={i} className="flex items-start space-x-2 mb-2">
                  <CheckCircle2 className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-600">{endpoint}</p>
                </div>
              )) || <p className="text-sm text-gray-500">Not specified</p>}
            </div>

            {analysis.endpoints?.secondary && analysis.endpoints.secondary.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Secondary</h4>
                {analysis.endpoints.secondary.slice(0, 3).map((endpoint, i) => (
                  <div key={i} className="flex items-start space-x-2 mb-2">
                    <Circle className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-gray-600">{endpoint}</p>
                  </div>
                ))}
              </div>
            )}

            {analysis.endpoints?.exploratory && analysis.endpoints.exploratory.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Exploratory</h4>
                {analysis.endpoints.exploratory.map((endpoint, i) => (
                  <div key={i} className="flex items-start space-x-2 mb-2">
                    <Circle className="h-4 w-4 text-blue-300 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-gray-600">{endpoint}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Protocol Requirements (Phase 1 of the RTM feature) */}
      <div className="card p-6">
        <div className="flex items-start justify-between mb-4">
          {/* Header is its own button so the whole title row toggles collapse.
              The Re-extract button on the right is a separate button so its
              click doesn't bubble up and accidentally toggle the card. */}
          <button
            type="button"
            onClick={() => setRequirementsExpanded((v) => !v)}
            className="flex items-center space-x-3 text-left flex-1 -m-2 p-2 rounded hover:bg-gray-50"
            aria-expanded={requirementsExpanded}
          >
            <div className="h-10 w-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <ListChecks className="h-5 w-5 text-indigo-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center space-x-2">
                <h3 className="font-semibold text-gray-900">Protocol Requirements</h3>
                {requirements.length > 0 && (
                  <span className="text-xs text-gray-500">
                    ({activeRequirementCount} active
                    {supersededRequirements.length > 0 && `, ${supersededRequirements.length} superseded`})
                  </span>
                )}
                <ChevronDown
                  className={`h-4 w-4 text-gray-400 transition-transform ${requirementsExpanded ? '' : '-rotate-90'}`}
                />
              </div>
              <p className="text-sm text-gray-500">
                Typed requirements that will be traced through CRFs, edit checks, and the database schema.
              </p>
            </div>
          </button>
          <button
            type="button"
            onClick={handleExtractRequirements}
            disabled={extractingRequirements || !canGenerate}
            title={!canGenerate ? "Your role doesn't allow generation on this project." : undefined}
            className="ml-3 inline-flex items-center space-x-2 px-3 py-1.5 text-sm rounded-md border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-60 disabled:cursor-not-allowed flex-shrink-0"
          >
            {extractingRequirements ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Extracting…</span>
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4" />
                <span>{requirements.length === 0 ? 'Extract Requirements' : 'Re-extract'}</span>
              </>
            )}
          </button>
        </div>

        {requirementsExpanded && (
          <>
            {extractRequirementsError && (
              <div className="mb-4 px-3 py-2 rounded bg-red-50 border border-red-200 text-sm text-red-700">
                {extractRequirementsError}
              </div>
            )}

            {requirements.length === 0 && !extractingRequirements && (
              <div className="px-4 py-6 text-center text-sm text-gray-500 bg-gray-50 rounded">
                No requirements extracted yet. Click <span className="font-medium">Extract Requirements</span> to create the typed
                requirements list from this protocol analysis.
              </div>
            )}

            {requirements.length > 0 && (
              <>
                <div className="space-y-5">
                  {groupedRequirements.map((group) => (
                    <div key={group.type}>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">
                        {group.label} <span className="text-xs font-normal text-gray-400">({group.items.length})</span>
                      </h4>
                      <ul className="space-y-1.5">
                        {group.items.map((req) => (
                          <li
                            key={req.id}
                            className="flex items-start space-x-2 text-sm text-gray-700"
                          >
                            <span className="inline-block min-w-[88px] px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 text-xs font-mono font-semibold flex-shrink-0">
                              {req.id}
                            </span>
                            <span className="flex-1">
                              {req.text}
                              {req.version > 1 && (
                                <span className="ml-2 inline-block px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-xs">
                                  v{req.version}
                                </span>
                              )}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>

                {supersededRequirements.length > 0 && (
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => setShowSupersededRequirements((v) => !v)}
                      className="flex items-center space-x-1 text-xs text-gray-500 hover:text-gray-700"
                    >
                      {showSupersededRequirements ? (
                        <ChevronDown className="h-3 w-3" />
                      ) : (
                        <ChevronRight className="h-3 w-3" />
                      )}
                      <span>
                        {showSupersededRequirements ? 'Hide' : 'Show'} {supersededRequirements.length} superseded requirement
                        {supersededRequirements.length === 1 ? '' : 's'}
                      </span>
                    </button>
                    {showSupersededRequirements && (
                      <ul className="mt-3 space-y-1.5">
                        {supersededRequirements.map((req) => (
                          <li
                            key={req.id}
                            className="flex items-start space-x-2 text-xs text-gray-400"
                          >
                            <span className="inline-block min-w-[88px] px-2 py-0.5 rounded bg-gray-100 text-gray-500 font-mono font-semibold flex-shrink-0">
                              {req.id}
                            </span>
                            <span className="flex-1 line-through">
                              {req.text}
                              {req.changeReason && (
                                <span className="ml-2 not-italic">({req.changeReason})</span>
                              )}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Safety Section */}
      {analysis.safety && (
        <div className="card p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="h-10 w-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Shield className="h-5 w-5 text-orange-600" />
            </div>
            <h3 className="font-semibold text-gray-900">Safety Monitoring</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Assessments</h4>
              <div className="space-y-1">
                {analysis.safety.assessments?.slice(0, 5).map((assessment, i) => (
                  <div key={i} className="text-sm text-gray-600 bg-gray-50 rounded px-3 py-2">
                    {assessment}
                  </div>
                )) || <p className="text-sm text-gray-500">Not specified</p>}
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Reporting Requirements</h4>
              <div className="space-y-1">
                {analysis.safety.reporting?.slice(0, 5).map((req, i) => (
                  <div key={i} className="text-sm text-gray-600 bg-gray-50 rounded px-3 py-2">
                    {req}
                  </div>
                )) || <p className="text-sm text-gray-500">Not specified</p>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Protocol Amendments — the version history, an amendment coming in, and
          adoption. Rendered only once the study has an analysis, because an
          amendment amends something: with no protocol on file there is nothing
          to compare against and `compareProtocolsV2` says so rather than
          guessing. */}
      {projectId && <AmendmentsPanel projectId={projectId} />}

      {/* Protocol Coverage Validation card REMOVED 2026-07-24 — the legacy
          percentage scorer was retired; the Verification tab is the single,
          deterministic checker for protocol-vs-artifact consistency. */}

      {/* Actions */}
      <div className="flex justify-between items-center pt-4">
        <button
          onClick={() => setShowUploader(true)}
          disabled={!canUpload}
          title={!canUpload ? "Your role doesn't allow uploads on this project." : undefined}
          className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Re-upload Protocol
        </button>
      </div>

      {/* Upload Modal */}
      {showUploader && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-elevated max-w-md w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Re-upload Protocol</h3>
              <button
                onClick={() => { setShowUploader(false); setSelectedFile(null); }}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            {!selectedFile ? (
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-primary-400 hover:bg-primary-50 transition-colors"
              >
                <Upload className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">Click to select a PDF or Word file</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,.doc"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <FileText className="h-8 w-8 text-primary-600" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{selectedFile.name}</p>
                    <p className="text-sm text-gray-500">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>

                {uploadProgress && (
                  <div className="flex items-center space-x-2 p-3 bg-primary-50 rounded-lg">
                    {uploading ? (
                      <Loader2 className="h-4 w-4 animate-spin text-primary-600" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    )}
                    <span className="text-sm text-primary-700">{uploadProgress}</span>
                  </div>
                )}

                <div className="flex space-x-3">
                  <button
                    onClick={() => { setShowUploader(false); setSelectedFile(null); }}
                    disabled={uploading}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpload}
                    disabled={uploading || !canUpload}
                    title={!canUpload ? "Your role doesn't allow uploads on this project." : undefined}
                    className="flex-1 px-4 py-2 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {uploading ? 'Processing...' : 'Upload & Analyze'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
